import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Minus, Plus, X, ShieldCheck, MapPin, Banknote, ArrowRight, Upload, CheckCircle2, CreditCard, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatRupiah, koperasiInfo } from "@/lib/dummyData";
import { getMidtransConfig, payOrderWithMidtrans } from "@/lib/midtrans";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { CartAPI, OrderAPI, ProductAPI, UploadAPI, PaymentAPI } from "@/lib/api";

// Baca file gambar dari <input type=file> menjadi data URL base64 (untuk diunggah ke backend).
const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// /keranjang (portal) untuk tamu; anggota yang sudah login diarahkan ke /anggota/keranjang
// agar navigasi keranjang tidak bercampur antara area publik dan ruang anggota.
export default function CartPage({ embedded = false }) {
  const { user, loading } = useAuth();
  if (!embedded && !loading && user) return <Navigate to="/anggota/keranjang" replace />;
  return <Cart embedded={embedded} />;
}

function Cart({ embedded }) {
  const { user } = useAuth();
  const { guestItems, setGuestQty, removeGuest, refreshServer } = useCart();
  const navigate = useNavigate();

  // --- State keranjang ---
  const [items, setItems] = useState([]);        // [{ product_id, name, image, quantity, unit_price, line_total }]
  const [subtotal, setSubtotal] = useState(0);   // subtotal dari server (sudah termasuk harga anggota)
  const [loading, setLoading] = useState(true);
  const [imgMap, setImgMap] = useState({});      // peta gambar produk (id -> url) untuk tampilan

  // --- State checkout ---
  // Default pengiriman: Kurir Toko. Pembayaran: Midtrans bila aktif, selain itu
  // Transfer Manual (bila rekening dikonfigurasi). Dompet anggota tidak dipakai untuk belanja.
  const manualEnabled = !!koperasiInfo.bankAccount;
  const [shipping, setShipping] = useState("reg");
  const [payment, setPayment] = useState(manualEnabled ? "manual" : null);
  const [midtransEnabled, setMidtransEnabled] = useState(false);
  const [payConfigLoaded, setPayConfigLoaded] = useState(false);
  const [placing, setPlacing] = useState(false);

  // --- State pembayaran manual (upload bukti) ---
  const [manualOrder, setManualOrder] = useState(null); // pesanan PENDING yang menunggu bukti transfer
  const [proofFile, setProofFile] = useState(null);
  const [senderName, setSenderName] = useState("");
  const [bankName, setBankName] = useState("BCA");

  const ship = shipping === "reg" ? 12000 : shipping === "exp" ? 24000 : 0;
  const total = subtotal + ship;

  useEffect(() => {
    getMidtransConfig().then((cfg) => {
      if (cfg.enabled) { setMidtransEnabled(true); setPayment("midtrans"); }
      setPayConfigLoaded(true);
    });
  }, []);

  // Ambil gambar produk sekali (backend cart hanya mengembalikan nama & harga, bukan gambar).
  useEffect(() => {
    ProductAPI.list()
      .then((rows) => setImgMap(Object.fromEntries(rows.map((p) => [p.id, p.image]))))
      .catch(() => {});
  }, []);

  // Petakan keranjang tamu (lokal) ke bentuk baris yang dipakai tampilan.
  const guestLines = () => guestItems.map((g) => ({
    product_id: g.id,
    product_name: g.name,
    unit_price: g.price,
    quantity: g.quantity,
    image: g.image,
  }));

  // Muat isi keranjang. Tamu → dari localStorage, anggota → dari backend.
  const loadCart = async () => {
    if (!user) {
      const lines = guestLines();
      setItems(lines);
      setSubtotal(lines.reduce((a, l) => a + l.unit_price * l.quantity, 0));
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await CartAPI.get(); // { lines, subtotal, ... } — harga dihitung di server
      setItems(data.lines || []);
      setSubtotal(data.subtotal || 0);
    } catch {
      toast.error("Gagal memuat keranjang");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCart(); /* eslint-disable-next-line */ }, [user, guestItems]);

  // Ubah kuantitas item (set nilai absolut ke server lalu muat ulang ringkasan harga).
  const changeQty = async (productId, nextQty) => {
    if (nextQty < 1) return removeItem(productId);
    if (!user) return setGuestQty(productId, nextQty);
    try {
      const data = await CartAPI.setItem(productId, nextQty);
      setItems(data.lines || []);
      setSubtotal(data.subtotal || 0);
      refreshServer();
    } catch {
      toast.error("Gagal memperbarui kuantitas");
    }
  };

  const removeItem = async (productId) => {
    if (!user) { removeGuest(productId); toast.success("Produk dihapus dari keranjang"); return; }
    try {
      const data = await CartAPI.removeItem(productId);
      setItems(data.lines || []);
      setSubtotal(data.subtotal || 0);
      refreshServer();
      toast.success("Produk dihapus dari keranjang");
    } catch {
      toast.error("Gagal menghapus produk");
    }
  };

  // Kirim pesanan ke backend. WALLET = langsung PAID; MANUAL_TRANSFER = PENDING lalu minta upload bukti.
  const placeOrder = async () => {
    if (!user) {
      toast.error("Silakan masuk untuk membuat pesanan");
      return navigate("/masuk");
    }
    if (!items.length) return toast.error("Keranjang masih kosong");
    if (!payment) return toast.error("Belum ada metode pembayaran yang aktif");

    setPlacing(true);
    try {
      const payload = {
        items: items.map((l) => ({ product_id: l.product_id, quantity: l.quantity })),
        payment_method: payment === "midtrans" ? "MIDTRANS" : "MANUAL_TRANSFER",
        shipping_cost: ship,
      };
      const { order, payment: snapPayment } = await OrderAPI.checkout(payload);

      if (payment === "midtrans") {
        // Pesanan dibuat PENDING → buka halaman pembayaran Midtrans (VA, QRIS, e-wallet, kartu).
        await loadCart();
        refreshServer();
        if (snapPayment?.error) {
          toast.error(snapPayment.error);
        } else {
          const { order: latest } = await payOrderWithMidtrans(order, snapPayment);
          if (latest.status === "PAID") toast.success(`Pembayaran pesanan ${order.order_number} berhasil`);
          else toast.info(`Pesanan ${order.order_number} menunggu pembayaran — bisa dilanjutkan dari halaman Pesanan`);
        }
        navigate("/anggota/pesanan");
      } else {
        // Transfer manual: pesanan menunggu bukti pembayaran dari user.
        setManualOrder(order);
        setSenderName(user.name || "");
        toast.info(`Pesanan ${order.order_number} dibuat — silakan unggah bukti transfer`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal membuat pesanan");
    } finally {
      setPlacing(false);
    }
  };

  // Unggah bukti transfer lalu kirim konfirmasi pembayaran manual untuk diverifikasi admin.
  const submitProof = async () => {
    if (!proofFile) return toast.error("Pilih file bukti transfer dulu");
    try {
      const dataUrl = await fileToDataUrl(proofFile);
      const url = await UploadAPI.image(proofFile.name, dataUrl);
      await PaymentAPI.submitProof({
        order_id: manualOrder.id,
        purpose: "ORDER",
        amount: Number(manualOrder.total),
        bank_name: bankName,
        sender_name: senderName,
        proof_url: url,
      });
      toast.success("Bukti terkirim. Pesanan menunggu verifikasi penjual.");
      setManualOrder(null);
      setProofFile(null);
      await loadCart();
      setTimeout(() => navigate("/anggota/pesanan"), 800);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal mengunggah bukti");
    }
  };

  // ====== TAMPILAN: langkah upload bukti pembayaran manual ======
  if (manualOrder) {
    return (
      <div className={embedded ? "max-w-2xl" : "mx-auto max-w-2xl px-5 lg:px-10 py-10 lg:py-16"}>
        <div className="bg-white rounded-3xl border border-stone-soft p-7 lg:p-10">
          <Badge className="bg-amber-warm/15 text-amber-700 border-0 rounded-full">Menunggu Pembayaran</Badge>
          <h1 className="font-display text-2xl lg:text-3xl font-bold text-ink mt-4">Unggah Bukti Transfer</h1>
          <p className="text-ink/75 mt-2 text-sm">
            Pesanan <b>{manualOrder.order_number}</b> telah dibuat. Transfer sejumlah{" "}
            <b>{formatRupiah(manualOrder.total)}</b> ke rekening koperasi, lalu unggah buktinya.
            Penjual produk akan memverifikasi bukti Anda.
          </p>

          {/* Rekening tujuan (dikonfigurasi lewat REACT_APP_BANK_* di frontend/.env) */}
          <div className="rounded-2xl bg-cream p-5 mt-6 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-ink/75">Bank Tujuan</span><span className="font-semibold text-ink">{koperasiInfo.bankName} {koperasiInfo.bankAccount}</span></div>
            <div className="flex justify-between"><span className="text-ink/75">Atas Nama</span><span className="font-semibold text-ink">{koperasiInfo.bankHolder}</span></div>
            <div className="flex justify-between"><span className="text-ink/75">Jumlah</span><span className="font-semibold text-forest">{formatRupiah(manualOrder.total)}</span></div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <div className="space-y-1.5">
              <Label className="text-xs text-ink/55 uppercase tracking-wider">Nama Pengirim</Label>
              <Input value={senderName} onChange={(e) => setSenderName(e.target.value)} className="bg-cream border-stone-soft rounded-lg" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-ink/55 uppercase tracking-wider">Bank Pengirim</Label>
              <Input value={bankName} onChange={(e) => setBankName(e.target.value)} className="bg-cream border-stone-soft rounded-lg" />
            </div>
          </div>

          {/* Input file bukti transfer */}
          <label htmlFor="proof-upload" className="mt-5 block border-2 border-dashed border-stone-soft rounded-2xl p-6 text-center cursor-pointer hover:bg-cream transition">
            <Upload size={22} className="mx-auto text-forest" />
            <div className="font-semibold text-sm text-ink mt-2">{proofFile ? proofFile.name : "Klik untuk pilih bukti transfer"}</div>
            <div className="text-xs text-ink/55 mt-1">JPG/PNG/WEBP, maks. 700 KB</div>
            <input id="proof-upload" type="file" accept="image/*" className="hidden" onChange={(e) => setProofFile(e.target.files?.[0] || null)} />
          </label>

          <div className="flex gap-3 mt-6">
            <Button variant="outline" className="rounded-full flex-1" onClick={() => setManualOrder(null)}>Nanti Saja</Button>
            <Button className="rounded-full flex-1 bg-forest hover:bg-forest-dark text-white" onClick={submitProof}>
              <CheckCircle2 size={16} /> Kirim Bukti
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ====== TAMPILAN: keranjang & checkout ======
  return (
    <div className={embedded ? "" : "mx-auto max-w-7xl px-5 lg:px-10 py-10 lg:py-14"}>
      <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">Keranjang & Checkout</h1>
      <p className="text-ink/75 mt-2 text-sm">Harga sudah menyesuaikan status keanggotaan Anda (dihitung di server).</p>

      {!user && (
        <div className="mt-6 rounded-2xl bg-amber-warm/10 border border-amber-warm/30 p-4 text-sm text-ink/80">
          Anda belum masuk. <Link to="/masuk" className="text-forest font-semibold underline">Masuk</Link> untuk memakai keranjang & harga anggota.
        </div>
      )}

      <div className={`grid lg:grid-cols-12 gap-6 lg:gap-8 ${embedded ? "mt-6" : "mt-10"}`}>
        <div className="lg:col-span-7 space-y-6">
          {/* Daftar produk dalam keranjang */}
          <section className="bg-white rounded-3xl border border-stone-soft p-6">
            <h2 className="font-display font-semibold text-lg text-ink">Produk ({items.length})</h2>
            <div className="mt-4 space-y-4">
              {loading ? (
                <div className="py-12 text-center text-ink/55 text-sm">Memuat keranjang…</div>
              ) : items.length === 0 ? (
                <div className="py-12 text-center">
                  <div className="text-3xl">🛒</div>
                  <p className="font-display font-semibold mt-3">Keranjang kosong</p>
                  <Link to={embedded ? "/anggota/toko" : "/toko"}><Button className="rounded-full bg-forest text-white mt-4">Mulai belanja</Button></Link>
                </div>
              ) : items.map((i) => (
                <div key={i.product_id} className="flex gap-4 items-center" data-testid={`cart-item-${i.product_id}`}>
                  <div className="w-20 h-20 rounded-xl overflow-hidden bg-cream shrink-0">
                    {(imgMap[i.product_id] || i.image) && <img src={imgMap[i.product_id] || i.image} alt={i.product_name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-ink text-sm">{i.product_name}</div>
                    <div className="font-display font-bold text-ink mt-1">{formatRupiah(i.unit_price)}</div>
                  </div>
                  {/* Pengatur kuantitas — memanggil backend agar keranjang tersimpan */}
                  <div className="flex items-center border border-stone-soft rounded-full shrink-0">
                    <button onClick={() => changeQty(i.product_id, i.quantity - 1)} className="w-8 h-8 grid place-items-center hover:bg-cream rounded-l-full"><Minus size={12} /></button>
                    <div className="w-8 text-center text-sm font-semibold">{i.quantity}</div>
                    <button onClick={() => changeQty(i.product_id, i.quantity + 1)} className="w-8 h-8 grid place-items-center hover:bg-cream rounded-r-full"><Plus size={12} /></button>
                  </div>
                  <button onClick={() => removeItem(i.product_id)} className="w-9 h-9 rounded-full hover:bg-terracotta/10 text-ink/55 hover:text-terracotta grid place-items-center" data-testid={`cart-remove-${i.product_id}`}>
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </section>

          {/* Metode pengiriman */}
          <section className="bg-white rounded-3xl border border-stone-soft p-6">
            <div className="flex items-center gap-2 mb-4">
              <MapPin size={18} className="text-forest" />
              <h2 className="font-display font-semibold text-lg text-ink">Metode Pengiriman</h2>
            </div>
            <RadioGroup value={shipping} onValueChange={setShipping} className="grid sm:grid-cols-3 gap-3">
              {[
                { v: "reg", l: "Kurir Toko (2-3 hari)", p: "Rp 12.000" },
                { v: "exp", l: "Kurir Toko Express", p: "Rp 24.000" },
                { v: "pick", l: "Ambil di Gerai", p: "Gratis" },
              ].map((s) => (
                <label key={s.v} htmlFor={`ship-${s.v}`} className={`flex items-center gap-3 border rounded-xl p-3 cursor-pointer transition ${shipping === s.v ? "border-forest bg-forest/5" : "border-stone-soft hover:bg-cream"}`}>
                  <RadioGroupItem value={s.v} id={`ship-${s.v}`} data-testid={`ship-${s.v}`} />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-ink">{s.l}</div>
                    <div className="text-xs text-ink/75">{s.p}</div>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </section>

          {/* Metode pembayaran — Midtrans (online) atau Transfer Manual (upload bukti) */}
          <section className="bg-white rounded-3xl border border-stone-soft p-6">
            <h2 className="font-display font-semibold text-lg text-ink">Metode Pembayaran</h2>
            <RadioGroup value={payment} onValueChange={setPayment} className="mt-4 grid sm:grid-cols-2 gap-3">
              {midtransEnabled && (
                <label htmlFor="pay-midtrans" className={`sm:col-span-2 flex items-center gap-3 border rounded-xl p-4 cursor-pointer transition ${payment === "midtrans" ? "border-forest bg-forest/5" : "border-stone-soft hover:bg-cream"}`}>
                  <RadioGroupItem value="midtrans" id="pay-midtrans" data-testid="pay-midtrans" />
                  <CreditCard size={18} className="text-forest" />
                  <div>
                    <div className="text-sm font-semibold text-ink">Bayar Online (Midtrans)</div>
                    <div className="text-xs text-ink/75">Virtual Account, QRIS, GoPay/ShopeePay, kartu kredit/debit — terverifikasi otomatis</div>
                  </div>
                </label>
              )}
              {manualEnabled && (
              <label htmlFor="pay-manual" className={`flex items-center gap-3 border rounded-xl p-4 cursor-pointer transition ${payment === "manual" ? "border-forest bg-forest/5" : "border-stone-soft hover:bg-cream"}`}>
                <RadioGroupItem value="manual" id="pay-manual" data-testid="pay-manual" />
                <Banknote size={18} className="text-forest" />
                <div>
                  <div className="text-sm font-semibold text-ink">Transfer Manual</div>
                  <div className="text-xs text-ink/75">Bayar & unggah bukti, diverifikasi admin</div>
                </div>
              </label>
              )}
            </RadioGroup>
            {!payment ? payConfigLoaded && (
              <div className="mt-3 flex items-start gap-2 rounded-xl bg-terracotta/10 text-terracotta text-sm p-3" data-testid="pay-none">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                Pembayaran online belum aktif. Silakan hubungi pengurus koperasi.
              </div>
            ) : (
              <p className="text-xs text-ink/55 mt-3">
                {payment === "midtrans"
                  ? "Setelah menekan tombol bayar, halaman pembayaran Midtrans akan terbuka. Status pesanan berubah otomatis menjadi Dibayar setelah pembayaran berhasil."
                  : "Pembayaran via Transfer Manual wajib mengunggah bukti transfer untuk diverifikasi."}
              </p>
            )}
          </section>
        </div>

        {/* Ringkasan pesanan */}
        <aside className="lg:col-span-5 lg:sticky lg:top-24 self-start">
          <div className="bg-forest text-cream rounded-3xl p-7 grain-overlay relative overflow-hidden">
            <h2 className="font-display font-semibold text-lg">Ringkasan Pesanan</h2>
            <div className="space-y-3 mt-5 text-sm">
              <div className="flex justify-between"><span className="text-cream/75">Subtotal ({items.length} produk)</span><span className="font-semibold">{formatRupiah(subtotal)}</span></div>
              <div className="text-[11px] text-cream/60 -mt-1">Sudah termasuk fee layanan koperasi 5% (mendukung SHU & operasional)</div>
              <div className="flex justify-between"><span className="text-cream/75">Ongkos kirim</span><span className="font-semibold">{formatRupiah(ship)}</span></div>
            </div>
            <Separator className="my-5 bg-cream/15" />
            <div className="flex justify-between items-baseline">
              <span className="text-cream/85">Total Bayar</span>
              <span className="font-display font-bold text-2xl text-amber-warm">{formatRupiah(total)}</span>
            </div>

            <Button
              onClick={placeOrder}
              disabled={placing || loading || items.length === 0 || !payment}
              className="w-full mt-6 rounded-full bg-amber-warm text-ink hover:bg-amber-warm/90 h-12 font-semibold"
              data-testid="cart-checkout"
            >
              {placing ? "Memproses…" : payment === "midtrans" ? "Bayar Sekarang" : "Buat Pesanan & Transfer"} <ArrowRight size={16} />
            </Button>

            <div className="mt-4 flex items-center gap-2 text-cream/70 text-xs">
              <ShieldCheck size={14} className="text-amber-warm" />
              Transaksi dicatat di buku besar (ledger) koperasi
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
